import { gql } from 'graphql-request';
import { graphQLClient } from '@devd-client/api';
import { getOrgId } from '@devd-client/devd/auth';
import {
  IssueTypeDistributionResponse,
  DeliverableTableResponse,
  DeliverableSignalsResponse,
  SprintMetricsWithStatusResponse,
  ContributorsAllocationResponse,
  TeamsAllocationResponse,
  DeliverableScenarioResponse,
} from '../helpers/Initiatives.types';

export const deliverableIssueTypeDistGraphql = async (
  initiativeId: string,
  distributionType: string,
  deliverableType: string,
  status: string
): Promise<IssueTypeDistributionResponse> =>
  await graphQLClient.request(
    gql`
      query GetIssueTypeDistribution(
        $filter: Filter!
        $distributionType: String!
      ) {
        IssueTypeDistribution(
          filter: $filter
          distributionType: $distributionType
        ) {
          key
          value
          color
        }
      }
    `,
    {
      filter: {
        orgId: getOrgId(),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        status,
        initiativeId,
        deliverableType,
      },
      distributionType,
    }
  );

export const getDeliverableTableGraphql = async (
  deliverableType: string,
  status: string,
  search: string,
  startDate: string,
  endDate: string,
  sprintId: string,
  teamId: string,
  pageNumber: number,
  pageSize: number
): Promise<DeliverableTableResponse> =>
  await graphQLClient.request(
    gql`
      query GetDeliverables(
        $filter: Filter!
        $pageNumber: Int!
        $pageSize: Int!
      ) {
        Deliverables(
          filter: $filter
          pageNumber: $pageNumber
          pageSize: $pageSize
        ) {
          deliverables {
            detail {
              name
              type
              id
              desc
              link
              status
              legend {
                color
                label
              }
              plannedDate
              estimatedDate
              startDate
              condition
              issueFilterConditions {
                conditions {
                  field
                  operator
                  value
                }
              }
            }
            velocity {
              data
              average
              cycleTime
            }
            scope {
              scopeChange
              totalTasks
              completedTasks
              todoData {
                totalCount
                epicCount
                taskCount
                bugCount
              }
              inProgressData {
                totalCount
                epicCount
                taskCount
                bugCount
              }
              doneData {
                totalCount
                epicCount
                taskCount
                bugCount
              }
              lastDoneDate
            }
            allocation {
              teams
              activeContributors
              effortHours
              avgAllocation
            }
            calculatedStatus {
              status {
                color
                label
              }
              projectedEndDate
            }
          }
          totalPages
          totalElements
          currentPage
        }
      }
    `,
    {
      filter: {
        orgId: getOrgId(),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        status,
        deliverableType,
        searchTerm: search,
        startDate,
        endDate,
        sprintId,
        teamId,
      },
      pageNumber,
      pageSize,
    }
  );

export const getDeliverableSignalsGraphql = async (
  initiativeId: string,
  deliverableType: string,
  status: string
): Promise<DeliverableSignalsResponse> =>
  await graphQLClient.request(
    gql`
      query GetDeliverableSignals($filter: Filter!) {
        DeliverableSignals(filter: $filter) {
          category
          score
          message
          link
          color
          tooltip
        }
      }
    `,
    {
      filter: {
        orgId: getOrgId(),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        status,
        initiativeId,
        deliverableType,
      },
    }
  );

export const getSprintMetricsWithStatus = async (
  filter: {
    orgId?: string;
    initiativeId: string;
    teamId: string;
    sprintIds: string[];
    startDate: string;
    endDate: string;
    status: string;
    type: string;
    projectsFilter: string[];
    sprintViewFilters: {
      isCompletionRate: boolean;
      isPlanningAccuracy: boolean;
    };
    deliverableType: string | null;
  },
  pageSize: number,
  pageNumber: number,
  bySp: boolean
): Promise<SprintMetricsWithStatusResponse> =>
  await graphQLClient.request(
    gql`
      query GetSprintMetricsWithStatus(
        $filter: Filter!
        $pageSize: Int
        $pageNumber: Int
        $bySp: Boolean
      ) {
        SprintMetricsWithStatus(
          filter: $filter
          bySp: $bySp
          pageSize: $pageSize
          pageNumber: $pageNumber
        ) {
          reports {
            sprintDetail {
              orgId
              sprintId
              sprintName
              sprintDuration
              startDate
              endDate
              status
              overCommitmentRate
              scopeCreepRate
              blockedIssues
              completionRate
            }
            sprintPlanning {
              tasks {
                name
                value
              }
              planningAccuracy
            }
            sprintProgress {
              tasks {
                status
                value
                color
              }
            }
            sprintTaskDist {
              tasksDistribution {
                status
                value
                color
              }
            }
            sprintVelocity {
              chart
            }
          }
          currentPage
          totalPages
          chartDataState
          zs
          chartError {
            message
            link
          }
        }
      }
    `,
    {
      filter: {
        ...filter,
        orgId: getOrgId(),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
      pageSize,
      pageNumber,
      bySp,
    }
  );

export const getContributorsAllocationGraphql = async (
  initiativeId: string,
  deliverableType: string,
  status: string,
  view: string
): Promise<ContributorsAllocationResponse> =>
  await graphQLClient.request(
    gql`
      query GetContributorsAllocation($filter: Filter) {
        ContributorsAllocation(filter: $filter) {
          contributor
          calculatedAllocation
          assignedAllocation
          lastActivityDate
          activityStartDate
        }
      }
    `,
    {
      filter: {
        orgId: getOrgId(),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        status,
        initiativeId,
        deliverableType,
        view,
      },
    }
  );

export const getTeamsAllocationGraphql = async (
  initiativeId: string,
  deliverableType: string,
  status: string
): Promise<TeamsAllocationResponse> =>
  await graphQLClient.request(
    gql`
      query GetTeamsAllocation($filter: Filter) {
        TeamsAllocation(filter: $filter) {
          name
          allocation
          assignedAllocation
          contributors
        }
      }
    `,
    {
      filter: {
        orgId: getOrgId(),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        status,
        initiativeId,
        deliverableType,
      },
    }
  );

export const getDeliverableScenarioGraphql = async (
  deliverableId: string,
  deliverableType: string,
  averageAttention: number | null,
  remainingScope: number | null,
  developerCount: number | null
): Promise<DeliverableScenarioResponse> =>
  await graphQLClient.request(
    gql`
      query GetDeliverableScenarioDto($scenarioInputDto: ScenarioInputDto) {
        DeliverableScenarioDto(scenarioInputDto: $scenarioInputDto) {
          targetDate
          projectedEndDate
          remainingDuration
          remainingEffort
          activeContributors
          averageAttention
          scope
          completedScope
          data
          keys {
            key
            name
            color
            chartType
          }
          referenceLineData
        }
      }
    `,
    {
      scenarioInputDto: {
        orgId: getOrgId(),
        deliverableId,
        deliverableType,
        averageAttention,
        remainingScope,
        developerCount,
      },
    }
  );
